import React, { useEffect, useMemo, useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import axios from "axios";
import {
  CinturonesSeguridadVehiculoAsignacionURL,
  EmpresasURL,
  VehiculosURL,
} from "../../../API/apiurls";

export function CinturonModal({ show, onHide, onSave, initialData, title }) {
  const [empresas, setEmpresas] = useState([]);
  const [vehiculos, setVehiculos] = useState([]);
  const [posiciones, setPosiciones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    id: null,
    serialNumero: "",
    nombre: "",
    empresaId: "",
    vehiculoId: "",
    posicionId: "",
    asiento: "",
    observacion: "",
    activo: true,
  });

  useEffect(() => {
    if (!show) return;
    setFormData({
      id: initialData?.id ?? null,
      serialNumero: initialData?.serialNumero ?? "",
      nombre: initialData?.nombre ?? "",
      empresaId: String(initialData?.empresasModel?.id ?? ""),
      vehiculoId: String(initialData?.vehiculosModel?.id ?? ""),
      posicionId: String(initialData?.posicionModel?.id ?? ""),
      asiento: initialData?.asiento ?? "",
      observacion: initialData?.observacion ?? "",
      activo: initialData?.activo ?? true,
    });
  }, [show, initialData]);

  useEffect(() => {
    if (!show) return;
    const loadEmpresas = async () => {
      try {
        setLoading(true);
        const res = await axios.get(EmpresasURL);
        const list = Array.isArray(res.data) ? res.data : [];
        setEmpresas(list);
        setFormData((prev) => ({ ...prev, empresaId: prev.empresaId || String(list[0]?.id ?? "") }));
      } catch (error) {
        console.error("Error cargando empresas:", error);
        setEmpresas([]);
      } finally {
        setLoading(false);
      }
    };
    loadEmpresas();
  }, [show]);

  useEffect(() => {
    if (!show || !formData.empresaId) return;
    const loadVehiculos = async () => {
      try {
        const resVehiculos = await axios.get(`${VehiculosURL}/xempresa/${formData.empresaId}`);
        setVehiculos(Array.isArray(resVehiculos.data) ? resVehiculos.data : []);
      } catch (error) {
        console.error("Error cargando vehículos de cinturón:", error);
        setVehiculos([]);
      }
    };
    loadVehiculos();
  }, [show, formData.empresaId]);

  useEffect(() => {
    if (!show) return;
    if (!formData.vehiculoId) {
      setPosiciones([]);
      setFormData((prev) => ({ ...prev, posicionId: "", asiento: prev.posicionId ? "" : prev.asiento }));
      return;
    }

    const loadPosicionesVehiculo = async () => {
      try {
        const resAsignacion = await axios.get(CinturonesSeguridadVehiculoAsignacionURL(formData.vehiculoId));
        const nextPosiciones = Array.isArray(resAsignacion.data?.posiciones) ? resAsignacion.data.posiciones : [];
        setPosiciones(nextPosiciones);
        setFormData((prev) => {
          const exists = nextPosiciones.some((item) => String(item.posicionId) === String(prev.posicionId));
          if (exists) {
            return prev;
          }
          return { ...prev, posicionId: "", asiento: "" };
        });
      } catch (error) {
        console.error("Error cargando posiciones del vehículo:", error);
        setPosiciones([]);
      }
    };
    loadPosicionesVehiculo();
  }, [show, formData.vehiculoId]);

  const posicionSeleccionada = useMemo(
    () => posiciones.find((item) => String(item.posicionId) === String(formData.posicionId)),
    [formData.posicionId, posiciones]
  );

  useEffect(() => {
    if (!posicionSeleccionada) return;
    setFormData((prev) => ({ ...prev, asiento: posicionSeleccionada.nombre }));
  }, [posicionSeleccionada]);

  const submit = () => {
    onSave({
      ...initialData,
      ...formData,
      empresaId: Number(formData.empresaId),
      vehiculoId: formData.vehiculoId ? Number(formData.vehiculoId) : null,
      posicionId: formData.posicionId ? Number(formData.posicionId) : null,
    });
  };

  return (
    <Modal show={show} onHide={onHide} centered contentClassName="bg-dark text-light border-secondary">
      <Modal.Header closeButton closeVariant="white" className="border-secondary">
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}><Spinner size="sm" /> Cargando...</div>
        ) : (
          <div style={{ display: "grid", gap: 14 }}>
            <Form.Group>
              <Form.Label>Empresa</Form.Label>
              <Form.Select
                value={formData.empresaId}
                onChange={(e) => setFormData((prev) => ({ ...prev, empresaId: e.target.value, vehiculoId: "", posicionId: "", asiento: "" }))}
                className="bg-dark text-light border-secondary"
              >
                {empresas.map((empresa) => <option key={empresa.id} value={empresa.id}>{empresa.nombre}</option>)}
              </Form.Select>
            </Form.Group>

            <Form.Group>
              <Form.Label>Serial</Form.Label>
              <Form.Control value={formData.serialNumero} onChange={(e) => setFormData((prev) => ({ ...prev, serialNumero: e.target.value }))} />
            </Form.Group>

            <Form.Group>
              <Form.Label>Nombre</Form.Label>
              <Form.Control value={formData.nombre} onChange={(e) => setFormData((prev) => ({ ...prev, nombre: e.target.value }))} />
            </Form.Group>

            <Form.Group>
              <Form.Label>Vehículo</Form.Label>
              <Form.Select
                value={formData.vehiculoId}
                onChange={(e) => setFormData((prev) => ({ ...prev, vehiculoId: e.target.value, posicionId: "", asiento: "" }))}
                className="bg-dark text-light border-secondary"
              >
                <option value="">Seleccione un vehículo</option>
                {vehiculos.map((vehiculo) => <option key={vehiculo.id} value={vehiculo.id}>{vehiculo.nombre}</option>)}
              </Form.Select>
            </Form.Group>

            <Form.Group>
              <Form.Label>Posición</Form.Label>
              <Form.Select
                value={formData.posicionId}
                onChange={(e) => setFormData((prev) => ({ ...prev, posicionId: e.target.value }))}
                className="bg-dark text-light border-secondary"
                disabled={!formData.vehiculoId}
              >
                <option value="">{formData.vehiculoId ? "Seleccione una posición" : "Primero seleccione un vehículo"}</option>
                {posiciones.map((posicion) => <option key={posicion.posicionId} value={posicion.posicionId}>{posicion.nombre}</option>)}
              </Form.Select>
              {!!formData.vehiculoId && !posiciones.length && (
                <div style={{ fontSize: 12, color: '#cbd5e1', marginTop: 6 }}>
                  Ese vehículo no tiene posiciones sincronizadas todavía.
                </div>
              )}
            </Form.Group>

            <Form.Group>
              <Form.Label>Asiento</Form.Label>
              <Form.Control value={formData.asiento} onChange={(e) => setFormData((prev) => ({ ...prev, asiento: e.target.value }))} readOnly={Boolean(posicionSeleccionada)} />
            </Form.Group>

            <Form.Group>
              <Form.Label>Observación</Form.Label>
              <Form.Control as="textarea" rows={3} value={formData.observacion} onChange={(e) => setFormData((prev) => ({ ...prev, observacion: e.target.value }))} />
            </Form.Group>

            <Form.Check type="switch" label="Activo" checked={Boolean(formData.activo)} onChange={(e) => setFormData((prev) => ({ ...prev, activo: e.target.checked }))} />
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="border-secondary">
        <Button variant="secondary" onClick={onHide}>Cancelar</Button>
        <Button variant="primary" onClick={submit}>Guardar</Button>
      </Modal.Footer>
    </Modal>
  );
}
